import analyticsApiClient from '../services/analytics';
import { useStateDispatch, useStateSelector } from '../store/';
import { updateLoadingState } from '../reducers/app/appReducer';
import {
  getPendingTimeSlots,
  loadTimeSlotsData,
} from '../reducers/timeslots/timeSlotsReducer';
import {
  type AvailableService,
  type ReplacementService,
  type Slot,
} from '../services/appointment/model';
import {
  selectAppOperation,
  selectSelectedOriginalService,
} from '../reducers/selectors';
import { getNumberOfDaysWithPendingTimeSlots } from '../helpers/timeSlotHelper';
import { sendEventToOptimizely } from '../optimizely';

export type GetAvailableTimeslotsArgs = [string];
export type HandlePendingSlotSelected = (date: string) => void;

export default function useGetAvailableTimeslots(): {
  onRetry: (fromDate?: string) => void;
  handlePendingSlotSelected: HandlePendingSlotSelected;
} {
  const dispatch = useStateDispatch();
  const { sendEventToEpisod } = analyticsApiClient();
  const appOperation = useStateSelector(selectAppOperation);
  const serviceId = useStateSelector((state) => state.app.selected.serviceId);
  const zipCode = useStateSelector((state) => state.search.postCode);
  const selectedLanguage = useStateSelector(
    (state) => state.app.selected.language,
  );
  const originalService = useStateSelector(selectSelectedOriginalService);
  const { replacementService } = originalService || {};
  const originalSlotsObjects = useStateSelector(
    (state) => state.timeSlots.availableTimeSlots,
  );
  const replacementSlotsObjects = useStateSelector(
    (state) => state.timeSlots.replacementServiceAvailableTimeSlots,
  );

  const fetchTimeSlots = async (fromDate?: string): Promise<void> => {
    try {
      dispatch(updateLoadingState(true));
      const result = await dispatch(
        loadTimeSlotsData({ from: fromDate }),
      ).unwrap();

      if (!result.hasOriginalTimeSlots) {
        const eventPrefix = result.hasReplacementServiceTimeSlots
          ? `no_time_windows_with_replacement_service`
          : `no_time_windows`;
        const eventName = `${eventPrefix}_${serviceId}_${appOperation}_appointment`;
        sendEventToEpisod(eventName);
        sendEventToOptimizely(eventName);
      }
    } catch (error) {
      // fail fetching time slots silently, error state will be handled by showing retry button
    } finally {
      dispatch(updateLoadingState(false));
    }
  };

  function handlePendingSlotSelected(date: string): void {
    const storesWithSlots = Object.entries(originalSlotsObjects);
    const replacementStoresWithSlots = Object.entries(replacementSlotsObjects);

    const updatePendingSlots =
      (
        isReplacementService: boolean,
        service?: AvailableService | ReplacementService,
      ) =>
      ([nextStoreId, nextSlots]: [string, Slot[]]): void => {
        const selectedSlotIndex = nextSlots.findIndex(
          (slot) => slot.date === date && slot.status === 'pending',
        );
        if (selectedSlotIndex >= 0 && service) {
          const numberOfDays = getNumberOfDaysWithPendingTimeSlots(
            nextSlots,
            selectedSlotIndex,
          );
          const pendingSelectedSlot = nextSlots[selectedSlotIndex];
          dispatch(
            getPendingTimeSlots({
              from: date,
              zipCode,
              serviceId: service.serviceId,
              location: service.location,
              storeId: nextStoreId,
              serviceCode: service.serviceCode,
              isReplacementService,
              numberOfDays,
              pendingSelectedSlot,
              language: selectedLanguage || undefined,
            }),
          );
        }
      };

    storesWithSlots.forEach(updatePendingSlots(false, originalService));
    replacementStoresWithSlots.forEach(
      updatePendingSlots(true, replacementService),
    );
  }

  return {
    onRetry: fetchTimeSlots,
    handlePendingSlotSelected,
  };
}
